import React from 'react';
import { ArrowLeft, Home, Image, Info } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const changelogData = [
  {
    date: 'Dec 6, 2025',
    changes: [
      { type: 'feature', text: 'New About page explaining our research mission and why it\'s free' },
      { type: 'feature', text: 'Feedback page for bug reports and feature requests' },
      { type: 'feature', text: 'AI Write button - generate descriptions when publishing to gallery' },
      { type: 'feature', text: 'Optional AI chat sharing when publishing sketches' },
      { type: 'feature', text: 'Dynamic sitemap with all gallery sketches for better SEO' },
      { type: 'fix', text: 'Console errors now show correct filename (sketch.js:82 instead of UUID)' },
      { type: 'ui', text: 'Navigation links on all pages (IDE, Gallery, About, Changelog)' },
      { type: 'ui', text: 'OG images for social sharing on gallery sketches' }
    ]
  },
  {
    date: 'Dec 1, 2025',
    changes: [
      { type: 'fix', text: 'Fixed AI responses appearing duplicated' }
    ]
  },
  {
    date: 'Nov 29, 2025',
    changes: [
      { type: 'feature', text: 'Chat history browser - revisit past AI conversations' },
      { type: 'feature', text: 'Get notified when AI finishes (even if you close the tab)' }
    ]
  },
  {
    date: 'Nov 28, 2025',
    changes: [
      { type: 'feature', text: 'Command palette (Ctrl+K) for quick actions' },
      { type: 'feature', text: 'Remix sketches from gallery' },
      { type: 'ui', text: 'IDE theme improvements' }
    ]
  },
  {
    date: 'Nov 27, 2025',
    changes: [
      { type: 'feature', text: 'Gallery button in header for quick access' }
    ]
  },
  {
    date: 'Nov 26, 2025',
    changes: [
      { type: 'feature', text: '"Apply All Code" button - apply all AI suggestions at once' },
      { type: 'feature', text: 'Publish to Gallery with auto-thumbnails' },
      { type: 'fix', text: 'Fixed Windows line ending issues' }
    ]
  },
  {
    date: 'Nov 25, 2025',
    changes: [
      { type: 'feature', text: 'Smarter AI responses' },
      { type: 'fix', text: 'Fixed external libraries (ml5.js, etc.) not loading' }
    ]
  },
  {
    date: 'Nov 24, 2025',
    changes: [
      { type: 'fix', text: 'Fixed sketches not rendering correctly' }
    ]
  },
  {
    date: 'Nov 22, 2025',
    changes: [
      { type: 'feature', text: 'Better error console with clear stack traces' },
      { type: 'fix', text: 'Fixed camera/microphone access in sketches' }
    ]
  },
  {
    date: 'Nov 20, 2025',
    changes: [
      { type: 'feature', text: 'Thumbs up/down to rate AI responses' },
      { type: 'feature', text: 'Added ml5.js computer vision library support' }
    ]
  },
  {
    date: 'Nov 17, 2025',
    changes: [
      { type: 'launch', text: 'Launch: AI-powered p5.js editor with sync to editor.p5js.org' }
    ]
  }
];

const typeIcons = {
  feature: '✨',
  fix: '🐛',
  ui: '🎨',
  launch: '🚀'
};

export default function ChangelogPage() {
  const navigate = useNavigate();

  // Enable scrolling (override global overflow:hidden)
  React.useEffect(() => {
    document.body.style.overflow = 'auto'
    document.documentElement.style.overflow = 'auto'
    return () => {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
    }
  }, [])

  return (
    <div className="bg-black" 
         style={{ fontFamily: "'Courier New', monospace", height: '100vh', overflowY: 'auto' }}>
      <Helmet>
        <title>Changelog | P5 AI Studio - Vibe Coding Updates</title>
        <meta name="description" content="See what's new in P5 AI Studio. Latest features for vibe coding, creative coding, and p5.js generative art with AI assistants GPT-4, Claude, and Gemini." />
        <meta property="og:title" content="Changelog | P5 AI Studio Vibe Coding Updates" />
        <meta property="og:description" content="Latest features for vibe coding and creative coding with AI." />
        <meta property="og:url" content="https://p5js.ai/changelog" />
        <link rel="canonical" href="https://p5js.ai/changelog" />
      </Helmet>
      
      {/* CRT Scanline Effect - fixed but doesn't block scroll */}
      <div className="fixed inset-0 pointer-events-none z-10" 
           style={{
             background: 'repeating-linear-gradient(0deg, rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.15) 1px, transparent 1px, transparent 2px)'
           }}></div>
      
      {/* Content - scrollable */}
      <div id="main-content" className="relative z-0 max-w-2xl mx-auto p-6 pb-16 text-green-500" 
           style={{ 
             textShadow: '0 0 5px rgba(0, 255, 0, 0.7)',
             background: 'radial-gradient(ellipse at center, rgba(0, 20, 0, 0.9) 0%, rgba(0, 10, 0, 0.95) 100%)',
             minHeight: '100vh'
           }}>
        
        {/* Navigation */}
        <nav className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-green-400 hover:text-green-300 transition"
            style={{ textShadow: '0 0 8px rgba(0, 255, 0, 0.9)' }}
          >
            <ArrowLeft size={20} />
            <span>Back to IDE</span>
          </button>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/" className="text-green-600 hover:text-green-400 transition flex items-center gap-1">
              <Home size={14} /> IDE
            </Link>
            <Link to="/gallery/" className="text-green-600 hover:text-green-400 transition flex items-center gap-1">
              <Image size={14} /> Gallery
            </Link>
            <Link to="/about" className="text-green-600 hover:text-green-400 transition flex items-center gap-1">
              <Info size={14} /> About
            </Link>
          </div>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-green-400 mb-2" 
              style={{ textShadow: '0 0 10px rgba(0, 255, 0, 0.9)' }}>
            <span className="text-green-300">&gt;</span> CHANGELOG
          </h1>
          <p className="text-green-600">What's new in P5JS AI Studio</p>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-8 text-sm text-green-600 border-b border-green-900 pb-4">
          <span>✨ Feature</span>
          <span>🐛 Bug fix</span>
          <span>🎨 UI</span>
          <span>🚀 Launch</span>
        </div>

        {/* Changelog entries */}
        <div className="space-y-8">
          {changelogData.map((entry, idx) => (
            <div key={idx} className="border-l-2 border-green-800 pl-4">
              <h2 className="text-green-400 font-semibold text-lg mb-3">{entry.date}</h2>
              <ul className="space-y-2">
                {entry.changes.map((change, changeIdx) => (
                  <li key={changeIdx} className="text-green-500">
                    <span className="mr-2">{typeIcons[change.type]}</span>
                    {change.text}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Footer */}
        <p className="text-green-400 mt-12 animate-pulse text-center">
          █<span className="invisible">_</span>
        </p>
      </div>
    </div>
  );
}
